import type { BillingState } from '@/state/billing/useBillingState';
import type { BillingSettingsManager } from '@/state/billing/useBillingSettings';
import { BILLING_PLAN_CATALOGUE } from 'definitions/billing-plans';
import { Button } from '@/components/ui/button';
import { cn } from 'helpers/cn';
import { formatBillingDate, statusLabel } from '@/state/billing/billing-format';
import { MICRO_LABEL_SHAPE, Panel } from '../../ConsolePanel';
import { SectionStatusLine } from './SectionStatusLine';

type CurrentPlanCardProps = { manager: BillingSettingsManager };

const STATUS_TONE: Record<string, string> = {
  active: 'border-ok-line text-ok',
  trialing: 'border-sel-line text-sel',
};

/**
 * Mode-aware: card, free and invoice each tell a different story, and only
 * 'card' has a Polar subscription behind it. It describes the account on show: the
 * caller's own, or, for an agency, the client chosen in the Clients table (each client
 * has a plan of its own). An agency's own account shows the agency fee instead of a
 * plan. A client's own users are told who pays and offered nothing to buy.
 *
 * With a live plan the action is the portal (card, invoices, cancelling); the
 * plan itself is chosen and changed in the plans panel below. Members see the
 * plan but not the actions, which are Owner/Admin only.
 */
export const CurrentPlanCard = ({ manager }: CurrentPlanCardProps) => {
  const { state, canManageBilling, isActivating, checkout, selectedClientName, accountId } = manager;
  const { billingState } = state;

  const isLive = billingState?.agencyFee ? billingState.agencyFee.live : billingState?.hasLiveSubscription;
  const actions = (() => {
    if (!billingState || !billingState.isPayer || billingState.billingMode !== 'card' || !canManageBilling) {
      return undefined;
    }
    if (isLive) {
      return (
        <Button size="sm" variant="outline" className="h-8 rounded-lg text-body" onClick={checkout.openBillingPortal}>
          Manage billing
        </Button>
      );
    }
    // Without a plan, the panel below is where one is chosen.
    if (isActivating) return <span className="supporting">Activating your subscription...</span>;
    return undefined;
  })();

  return (
    <Panel
      tourAnchor="billing-plan"
      title={accountId ? `Plan: ${selectedClientName ?? 'client'}` : billingState?.agencyFee ? 'Agency fee' : 'Plan'}
      description={
        accountId
          ? "This client's subscription. You pay for it on your own billing account."
          : 'Your subscription and how this account is billed.'
      }
      actions={actions}
    >
      {!billingState ? (
        <SectionStatusLine
          status={state}
          loadingText="Loading your plan..."
          errorText="Your plan could not be loaded."
        />
      ) : (
        <PlanBody billingState={billingState} canManageBilling={canManageBilling} />
      )}
    </Panel>
  );
};

type PlanBodyProps = { billingState: BillingState; canManageBilling: boolean };

const PlanBody = ({ billingState, canManageBilling }: PlanBodyProps) => {
  const { billedBy, isPayer, billingMode, graceEndsAt, agencyFee, pendingChange } = billingState;
  // An agency has a fee rather than a plan; everything else reads the account's own plan.
  const subscriptionStatus = agencyFee ? agencyFee.status : billingState.subscriptionStatus;
  const trialEnd = agencyFee ? agencyFee.trialEnd : billingState.trialEnd;
  const interval = agencyFee ? agencyFee.interval : billingState.interval;
  const plan = agencyFee ? null : billingState.plan;

  if (billingMode === 'free') return <p className="supporting m-0">This account is not billed.</p>;
  if (billingMode === 'invoice') {
    return (
      <div className="grid gap-1">
        {plan && (
          <span className="text-foreground text-body font-medium">{BILLING_PLAN_CATALOGUE[plan].name} plan</span>
        )}
        <p className="supporting m-0">Billed by invoice. Contact us with any questions.</p>
      </div>
    );
  }

  if (billingState.planIncluded && plan) {
    return (
      <div className="grid gap-1">
        <span className="text-foreground text-body font-medium">{BILLING_PLAN_CATALOGUE[plan].name}, included</span>
        <p className="supporting m-0">
          This is your own account, included in the agency fee at no charge, with its sessions and AI credits. Your
          clients' accounts each have a plan of their own.
        </p>
      </div>
    );
  }

  if (!subscriptionStatus) {
    if (billedBy && !isPayer) {
      return (
        <div className="grid gap-1">
          <p className="text-foreground m-0 text-body font-medium">Billed through {billedBy.name}</p>
          <p className="supporting m-0">
            {billedBy.name} has not chosen a plan for this account yet, so it is read-only until it does.
          </p>
        </div>
      );
    }
    if (agencyFee) {
      return (
        <p className="supporting m-0">
          No agency fee yet.{' '}
          {canManageBilling
            ? billingState.agencyFeeTrialAvailable
              ? 'Choose it below to start the 12-day free trial.'
              : 'Choose it below to start the agency features again.'
            : 'An account Owner or Admin can choose it.'}
        </p>
      );
    }
    return (
      <p className="supporting m-0">
        No plan yet.{' '}
        {canManageBilling && isPayer
          ? billingState.trialAvailable
            ? 'Choose one below to start the 12-day free trial.'
            : 'Choose one below to start serving again.'
          : 'An account Owner or Admin can choose a plan.'}
      </p>
    );
  }

  return (
    <div className="grid gap-2">
      {billedBy && !isPayer && (
        <p className="text-foreground m-0 text-body font-medium">
          Billed through {billedBy.name}, which manages the plan, payment and invoices for this account.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {agencyFee && (
          <span className="text-foreground text-body font-medium">
            Agency fee, {interval === 'year' ? 'annual' : 'monthly'}
          </span>
        )}
        {plan && (
          <span className="text-foreground text-body font-medium">
            {BILLING_PLAN_CATALOGUE[plan].name}, {interval === 'year' ? 'annual' : 'monthly'}
          </span>
        )}
        <span
          className={cn(
            MICRO_LABEL_SHAPE,
            'rounded-pill inline-flex w-fit border px-2 py-0.5',
            STATUS_TONE[subscriptionStatus] ?? 'border-danger-line text-danger',
          )}
        >
          {statusLabel(subscriptionStatus)}
        </span>
        {subscriptionStatus === 'trialing' && trialEnd && (
          <span className="supporting">First charge {formatBillingDate(trialEnd)}</span>
        )}
      </div>
      {pendingChange && (
        <p className="supporting m-0">
          Changes to {BILLING_PLAN_CATALOGUE[pendingChange.plan].name} (
          {pendingChange.interval === 'year' ? 'annual' : 'monthly'}) on {formatBillingDate(pendingChange.appliesAt)}.
        </p>
      )}
      {subscriptionStatus === 'past_due' && (
        <p className="text-danger m-0 text-meta">
          {graceEndsAt
            ? `The last payment failed. Experiments and surveys stop serving on ${formatBillingDate(graceEndsAt)} unless the card is updated.`
            : 'The last payment failed. Update the card to keep experiments and surveys serving.'}
        </p>
      )}
      {!canManageBilling && <p className="supporting m-0">Billing is managed by this account's Owners and Admins.</p>}
    </div>
  );
};
